import { computed, onMounted, onUnmounted, ref } from 'vue';
import axios from 'axios';
import { espoRpcUrl } from '@/services/config';
import useCollectionVaults from '@/composables/useCollectionVaults';
import { loadHistory, normalizeDispenser, normalizeOrder, normalizeHorizon, normalizeTrade, matchListings, matchEmblemAsk, tradeStats } from '@/services/market.mjs';
const sources = ref({}), refreshing = ref(false), checkedAt = ref(null), clock = ref(Date.now());
let pending, timer, subscribers = 0;
const rpc = async (method, params) => {
  if (!espoRpcUrl) throw new Error('not_configured');
  const { data } = await axios.post(espoRpcUrl, { jsonrpc: '2.0', id: 1, method: `xcp.${method}`, params }, { timeout: 15000 });
  if (!data.result?.ok) throw new Error('unavailable');
  return data.result;
};
const specs = [['dispensers', 'open'], ['orders', 'open'], ['dispenses', 'all'], ['matches', 'completed'], ['pool_matches', 'all']];
async function refresh() {
  if (pending) return pending;
  refreshing.value = true;
  pending = Promise.allSettled([
    ...specs.map(async ([type, status]) => {
      const rows = await loadHistory(rpc, type, status);
      return [type, { rows, ok: true, at: Date.now() }];
    }),
    axios.get('/market-api/external', { timeout: 20000 }).then(({ data }) => ['external', { ...data, at: Date.now(), ok: true }]),
  ]).then(results => {
    const next = {};
    results.forEach((result, i) => { const key = specs[i]?.[0] || 'external'; next[key] = result.status === 'fulfilled' ? result.value[1] : { ok: false, rows: [], at: Date.now() }; });
    sources.value = next; checkedAt.value = Date.now(); clock.value = Date.now();
  }).finally(() => { pending = null; refreshing.value = false; });
  return pending;
}
export default function useMarket() {
  const vaults = useCollectionVaults();
  onMounted(() => { subscribers++; if (subscribers === 1) { refresh(); timer = setInterval(() => { clock.value = Date.now(); refresh(); }, 60000); } });
  onUnmounted(() => { subscribers--; if (!subscribers) clearInterval(timer); });
  const fresh = key => sources.value[key]?.ok && clock.value - sources.value[key].at < 90000;
  const orders = computed(() => fresh('orders') ? sources.value.orders.rows.map(normalizeOrder).filter(Boolean) : []);
  const dispensers = computed(() => fresh('dispensers') ? sources.value.dispensers.rows.map(normalizeDispenser).filter(Boolean) : []);
  const external = computed(() => fresh('external') ? sources.value.external : null);
  const horizon = computed(() => external.value?.horizon?.ok ? external.value.horizon.listings.map(r => normalizeHorizon(r, clock.value)).filter(Boolean) : []);
  const listings = computed(() => [...dispensers.value, ...orders.value.filter(r => r.side === 'ask'), ...horizon.value]);
  const matches = computed(() => {
    const result = matchListings(vaults.locations.value, listings.value);
    for (const [id, asks] of Object.entries(matchEmblemAsk(vaults.locations.value, external.value?.ethereum, clock.value))) (result[id] ||= []).push(...asks);
    return result;
  });
  const trades = computed(() => specs.slice(2).flatMap(([key]) => fresh(key) ? sources.value[key].rows.map(row => normalizeTrade(row, key)).filter(Boolean) : []).sort((a, b) => b.time - a.time || b.block - a.block));
  const stats = computed(() => tradeStats(trades.value));
  const bitcoinReady = computed(() => !!(fresh('dispensers') && fresh('orders') && vaults.locations.value));
  const historyComplete = computed(() => specs.slice(2).every(([key]) => fresh(key)));
  const refreshAll = () => Promise.all([refresh(), vaults.refresh()]);
  return { vaults, sources, refreshing, checkedAt, orders, dispensers, listings, matches, trades, stats, external, bitcoinReady, historyComplete, refresh: refreshAll };
}
